import {
  DeviceActive,
  DeviceActiveStr,
  DeviceTypeId,
} from '@services/constants'
import { useConvertData } from '@services/device'
import { useParkingFeeRateTypeEnum } from '@services/parkingFee'
import {
  useAllSessions,
  useConvertParkingLotFullSession,
} from '@services/session'
import { convertData } from '@shared/innmaxLib/services'
import { convertTimestampToDuration } from '@shared/innmaxLib/utils/webHelper'
import dayjs from 'dayjs'
import moment from 'moment'
import { cond, isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAirportOccupancyFeeRoundingEnum,
  useDemandResponseTypeEnum,
  useOperationTypeEnum,
  useOrderStatusEnum,
  useOrderTypeEnum,
  usePaymentStatusEnum,
  usePaymentTypeEnum,
} from './operation.enum'
import {
  E_ParkingFeeDayType,
  ParkingFeeDayType,
  ParkingFeeDayTypeStr,
} from './operation.type'

export const useConvertParkingFeeList = (_res: any) => {
  const {
    deviceStatus,
    activeStr,
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    locationNs,
    sessionPath,
  } = useConvertData()

  const _data = _res?.data.map((item: any) => {
    if (Array.isArray(item.sessionId) && item.sessionId.length > 0) {
      return {
        ...item,
        sessionId: item.sessionId[0],
      }
    }
    return item
  })

  const res = {
    ..._res,
    data: _data,
  }

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...deviceStatus(x),
            ...activeStr(x),
            ...sessionName(x),
            ...expiredDate(x),
            ...installTime(x),
            ...updateTime(x),
            ...locationNs(x),
            ...sessionPath(x),
            properties: {
              ...x.properties,
            },
            dayTypeStr:
              ParkingFeeDayTypeStr[
                Object.keys(ParkingFeeDayType).find(
                  key =>
                    ParkingFeeDayType[key as keyof typeof ParkingFeeDayType] ===
                    x?.dayType
                ) as E_ParkingFeeDayType
              ],
            timeListStr: x?.timeList
              ?.map(
                (x: any) =>
                  `${moment(x.timeStart).format('HH:mm')}-${moment(
                    x.timeEnd
                  ).format('HH:mm')}`
              )
              .join('/'),
            pricingTypeStr: x?.pricingType === 3 ? '累進制' : '計時制',
            ruleList: x?.ruleList?.map((rule: any) => ({
              imeStart: rule.timeStart / 24 / 60 / 1000,
              timeEnd: rule.timeEnd / 24 / 60 / 1000,
            })),
          }
        },
      })(res),
    [res]
  )

  return dataSource
}

export const useConvertStandList = (res: any) => {
  const converter = useConvertStand()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter,
      })(res),
    [res]
  )

  return dataSource
}

export const useConvertParkingFeeDiscountList = (_res: any) => {
  const {
    deviceStatus,
    activeStr,
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    locationNs,
    sessionPath,
  } = useConvertData()

  const _data = _res?.data.map((item: any) => {
    if (Array.isArray(item.sessionId) && item.sessionId.length > 0) {
      return {
        ...item,
        sessionId: item.sessionId[0],
      }
    }
    return item
  })

  const res = {
    ..._res,
    data: _data,
  }

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...deviceStatus(x),
            ...activeStr(x),
            ...sessionName(x),
            ...expiredDate(x),
            ...installTime(x),
            ...updateTime(x),
            ...locationNs(x),
            ...sessionPath(x),
            properties: {
              ...x.properties,
            },
            dayTypeStr:
              ParkingFeeDayTypeStr[
                Object.keys(ParkingFeeDayType).find(
                  key =>
                    ParkingFeeDayType[key as keyof typeof ParkingFeeDayType] ===
                    x?.dayType
                ) as E_ParkingFeeDayType
              ],
            timeListStr: x?.timeList
              ?.map(
                (x: any) =>
                  `${moment(x.timeStart).format('HH:mm')}-${moment(
                    x.timeEnd
                  ).format('HH:mm')}`
              )
              .join('/'),
            pricingTypeStr: x?.pricingType === 3 ? '累進制' : '計時制',
            ruleList: x?.ruleList?.map((rule: any) => ({
              imeStart: rule.timeStart / 24 / 60 / 1000,
              timeEnd: rule.timeEnd / 24 / 60 / 1000,
            })),
          }
        },
      })(res),
    [res]
  )

  return dataSource
}

export const useConvertStand = () => {
  const {
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    locationNs,
    sessionPath,
  } = useConvertData()

  const { t } = useTranslation()

  const convertFullSession = useConvertParkingLotFullSession()

  const { byId: demandResponseTypeEnumById } = useDemandResponseTypeEnum({})
  const { byId: operationTypeEnumById } = useOperationTypeEnum()

  const converter = React.useCallback(
    (x: any) => {
      if (!x) {
        return {}
      }

      const statusStr = (d: any) => {
        if (isNil(d?.status)) {
          return {}
        }

        const active =
          d.status === -1
            ? DeviceActive.DELETE
            : d.status === 0
            ? DeviceActive.INACTIVE
            : DeviceActive.ACTIVE

        return {
          statusStr: t(`device:${DeviceActiveStr[active]}`),
        }
      }

      const fullSession = convertFullSession(x, 'id')

      return {
        ...x,
        ...statusStr(x),
        ...sessionName(x),
        ...expiredDate(x),
        ...installTime(x),
        ...updateTime(x),
        ...locationNs(x),
        ...sessionPath(x),
        deviceTypeId: DeviceTypeId[x.deviceType],
        chargingPoints: convertChargingPoints(x.chargingPoints),
        distName: fullSession?.distSession?.name || '-',
        cityName: fullSession?.citySession?.name || '-',
        properties: {
          ...x.properties,
          demandResponseTypeStr:
            demandResponseTypeEnumById[x.properties.demandResponseType]
              ?.label || '',
          operationTypeStr:
            operationTypeEnumById[x?.properties.operationType]?.label || '',
        },
      }
    },
    [convertFullSession]
  )

  return converter
}

export const useConvertIOccupancyFeeList = (res: any) => {
  const { t } = useTranslation()
  const { sessionName } = useConvertData()
  const { toStr } = useAirportOccupancyFeeRoundingEnum()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...sessionName(x),
            enableStr: x.enable ? '啟用' : '未啟用',
            roundingMethodStr: toStr(x.roundingMethod),
          }
        },
      })(res),
    [res, sessionName]
  )

  return dataSource
}

export const useConvertIOccupancyFee = (res: any) => {
  const { t } = useTranslation()
  const { sessionName } = useConvertData()

  const { toStr } = useAirportOccupancyFeeRoundingEnum()

  return React.useMemo(() => {
    if (!res) {
      return {}
    }

    return {
      ...res,
      ...sessionName(res),
      enableStr: res.enable ? '啟用' : '未啟用',
      roundingMethodStr: toStr(res.roundingMethod),
    }
  }, [res])
}

const convertChargingPoints = (chargingPoints: any[]) => {
  return chargingPoints?.map((chargingPoint: any) => {
    return {
      ...chargingPoint,
      connectors: chargingPoint.connectors.map(convertConnector),
    }
  })
}

const convertConnector = (connector: any) => {
  return {
    ...connector,
    statusStr: cond<any, any>([
      [s => s === 0, () => '可使用'],
      [s => s === 1, () => '充電中'],
      [s => s === 2, () => '維修中'],
    ])(connector?.status),
  }
}

export const useConvertIOrderList = (res: any) => {
  const { t } = useTranslation()
  const { sessionName } = useConvertData()
  const { byId } = useAllSessions()
  const { byId: byRateTypeId } = useParkingFeeRateTypeEnum({
    supportFreeRate: true,
  })

  const { byId: byPaymentTypeId } = usePaymentTypeEnum()

  const { byId: byPaymentStatusId } = usePaymentStatusEnum()

  const { byId: byOrderStatusId } = useOrderStatusEnum()

  const { byId: byOrderTypeId } = useOrderTypeEnum()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          const parkingLotSession = byId[x.sessionId]
          const areaSession = byId[parkingLotSession?.parentSessionId]
          const citySession = byId[areaSession?.parentSessionId]
          return {
            ...x,
            ...sessionName(x),
            cityName: citySession?.name,
            areaName: areaSession?.name,
            paymentTypeStr: byPaymentTypeId[x?.paymentType]?.label || '-',
            paymentStatusStr: byPaymentStatusId[x?.paymentStatus]?.label || '-',
            orderStatusStr: byOrderStatusId[x?.orderStatus]?.label || '-',
            orderTypeStr: byOrderTypeId[x?.orderType]?.label || '-',
            chargeStartAtStr: x?.chargeStartAt
              ? dayjs(x?.chargeStartAt).format('YYYY/MM/DD HH:mm:ss')
              : '-',
            chargeEndAtStr: x?.chargeEndAt
              ? dayjs(x?.chargeEndAt).format('YYYY/MM/DD HH:mm:ss')
              : '-',
            parkingStartAtStr: x?.parkingStartAt
              ? dayjs(x?.parkingStartAt).format('YYYY/MM/DD HH:mm:ss')
              : '-',
            parkingEndAtStr: x?.parkingEndAt
              ? dayjs(x?.parkingEndAt).format('YYYY/MM/DD HH:mm:ss')
              : '-',
            occupiedDurationStr: x?.occupiedDuration
              ? dayjs()
                  .startOf('day')
                  .add(x?.occupiedDuration, 'milliseconds')
                  .format('HH:mm')
              : '-',
            completeAtStr: x?.completeAt
              ? dayjs(x?.completeAt).format('YYYY/MM/DD HH:mm:ss')
              : '-',
            chargeInfo: {
              ...x.chargeInfo,
              chargeDurationStr: x?.chargeInfo?.chargeDuration
                ? convertTimestampToDuration(x?.chargeInfo?.chargeDuration)
                : '-',
            },
            parkingInfo: {
              ...x.parkingInfo,
              rateTypeStr: byRateTypeId[x?.parkingInfo?.rateType]?.label || '-',
              parkingDurationStr: x?.parkingInfo?.parkingDuration
                ? convertTimestampToDuration(x?.parkingInfo?.parkingDuration)
                : '-',
            },
          }
        },
      })(res),
    [res, sessionName]
  )

  return dataSource
}
